import { UPSELLS_ENABLED } from "@/lib/features";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, RotateCcw, Sparkles, ShoppingBag, Heart, Info, Shuffle, Lock, Loader2, Star, AlertTriangle, Bug, Copy, ChevronDown, Maximize2, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import type { Answers } from "./Questionnaire";
import DesignVariations from "./DesignVariations";
import DesignDebugPanel from "./DesignDebugPanel";
import SavedItemsSheet from "./SavedItemsSheet";
import { ToastAction } from "@/components/ui/toast";

const SAVED_PAGE_PATH = "/dashboard?tab=saved";
import SaveAfterViewPrompt from "./SaveAfterViewPrompt";
import TrackOnView from "./TrackOnView";
import UpsellDialog from "./UpsellDialog";
import { PROMO } from "@/lib/promo";
import { useSubscription } from "@/hooks/useSubscription";
import { useGenerationQuota } from "@/hooks/useGenerationQuota";
import NPSPrompt from "./NPSPrompt";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useDebugMode } from "@/hooks/useDebugMode";
import { useDebugPanel } from "@/hooks/useDebugPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNpsTriggers } from "@/hooks/useNpsTriggers";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { track, affiliateProps, productItemId, parsePriceNumber, useDebouncedTrack } from "@/lib/posthog";
import { trackBrevo, touchActivity } from "@/lib/brevo";
import type { DebugInfo } from "@/pages/Index";
import RefinePanel from "@/components/RefinePanel";
import RefinementsStrip from "@/components/RefinementsStrip";
import { useRefinements, type Refinement } from "@/hooks/useRefinements";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FURNITURE_CATEGORIES, resolveCategory, categorizeFromQuery, isJunkListing, type FurnitureCategory } from "@/lib/furnitureCategory";
import { captureSentryException } from "@/lib/sentry";

type EvalCheck = { name: string; status: "pass" | "warn" | "fail"; score: number; reason: string; model?: string; latency_ms?: number };
type Evaluation = {
  id: string;
  variant_id: string | null;
  overall_status: "pass" | "warn" | "fail";
  score: number;
  checks: EvalCheck[];
  created_at?: string;
  model_used?: string | null;
  evaluator_version?: string | null;
};


export interface Product {
  title: string;
  price: string;
  link: string | null;
  searchLink: string;
  thumbnail: string;
  source: string;
  rating?: number | null;
  reviews?: number | null;
  productLink?: string | null;
  designId?: string | null;
  furniture?: {
    item?: string;
    description?: string;
    searchQuery?: string;
    category?: string;
  } | null;
}


const StarRating = ({ rating, reviews }: { rating: number; reviews?: number | null }) => {
  const pct = Math.max(0, Math.min(5, rating)) / 5 * 100;
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="relative inline-block" aria-label={`${rating.toFixed(1)} out of 5`}>
        <div className="flex text-muted-foreground/40">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className="w-3.5 h-3.5" strokeWidth={1.5} />
          ))}
        </div>
        <div className="absolute inset-0 overflow-hidden flex text-foreground" style={{ width: `${pct}%` }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className="w-3.5 h-3.5 fill-current shrink-0" strokeWidth={1.5} />
          ))}
        </div>
      </div>
      <span className="text-xs text-muted-foreground">
        {rating.toFixed(1)}{typeof reviews === "number" && reviews > 0 ? ` (${reviews.toLocaleString()})` : ""}
      </span>
    </div>
  );
};

interface FurnitureItem {
  item: string;
  searchQuery: string;
  description?: string;
  category?: string;
}

interface DesignVariation {
  id: string;
  label: string;
  image: string;
}

export type ExistingItem = {
  name: string;
  decision: "keep" | "replace";
  reasoning: string;
};

interface DesignResultsProps {
  preview: string;
  aiImage: string;
  variations?: DesignVariation[];
  answers: Answers;
  products: Product[] | null;
  furnitureItems?: FurnitureItem[];
  overBudget?: boolean;
  totalEstimate?: number;
  designId?: string | null;
  generationId?: string | null;
  regeneratingVariantId?: string | null;
  debug?: DebugInfo | null;
  loadingProducts?: boolean;
  existingItems?: ExistingItem[];
  pickedVariantId: string;
  isPro: boolean;
  
  onCloneVariation?: (variantId: string) => void | Promise<void>;
  cloningVariantId?: string | null;
  onReset: () => void;
  onRegenerateVariant?: (variantId: string, opts?: { existingItems?: ExistingItem[] }) => void | Promise<void>;
  onUserAction?: (action: "variation_selected" | "refinement_submitted") => void;
}

const FEEDBACK_AFTER_SHUFFLES = 3;

const DesignResults = ({ preview, aiImage, variations, answers, products, furnitureItems = [], overBudget, totalEstimate, designId, generationId, regeneratingVariantId, debug, loadingProducts = false, existingItems = [], pickedVariantId, isPro, onCloneVariation, cloningVariantId, onReset, onRegenerateVariant, onUserAction }: DesignResultsProps) => {
  const debouncedFilterTrack = useDebouncedTrack("products_filter_changed", 400);
  const location = useLocation();
  const pricingHref = `/pricing?from=${encodeURIComponent(location.pathname + location.search)}`;

  const variants: DesignVariation[] = (variations && variations.length > 0)
    ? variations
    : [{ id: "design-1", label: "Design 1", image: aiImage }];
  // Picked variant is the source of truth; no in-page switching from results.
  const activeVariantId = variants.find((v) => v.id === pickedVariantId)?.id ?? variants[0].id;

  const [activeRefinement, setActiveRefinement] = useState<Refinement | null>(null);
  const [refineOpen, setRefineOpen] = useState(false);
  useEffect(() => {
    // Reset refinement whenever a new generation arrives
    setActiveRefinement(null);
  }, [variants.map((v) => v.image).join("|")]);

  const activeVariant = variants.find((v) => v.id === activeVariantId) ?? variants[0];
  const refinementMatchesActive = !!activeRefinement && (activeRefinement.variant_id ?? variants[0].id) === activeVariantId;
  const displayedAiImage = refinementMatchesActive ? activeRefinement!.image_url : activeVariant.image;

  const [showBefore, setShowBefore] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8);
  const [sheetOpen, setSheetOpen] = useState(false);
  type TabId = "shop" | "pieces" | "variations";
  const [activeTab, setActiveTab] = useState<TabId>("shop");

  // Per-item user decision (initialized from AI recommendation), plus which
  // item's reasoning is expanded. The parent owns the canonical existingItems
  // list; we mirror decisions locally so the user can toggle freely and only
  // commit on "Regenerate with my picks".
  const aiDefaultsKey = useMemo(
    () => existingItems.map((p) => `${p.name}:${p.decision}`).join("|"),
    [existingItems],
  );
  const [userDecisions, setUserDecisions] = useState<Record<string, "keep" | "replace">>(() =>
    Object.fromEntries(existingItems.map((p) => [p.name, p.decision])),
  );
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  // Keep the user's in-flight picks visible while a regen is running — the
  // parent briefly re-renders with the old list before the server response
  // lands, which would otherwise snap the toggles back.
  const picksInFlightRef = useRef(false);
  useEffect(() => {
    if (picksInFlightRef.current) return;
    setUserDecisions(Object.fromEntries(existingItems.map((p) => [p.name, p.decision])));
    setExpandedItem(null);
  }, [aiDefaultsKey]);
  const hasDecisionChanges = existingItems.some((p) => (userDecisions[p.name] ?? p.decision) !== p.decision);
  const regenWithPicks = () => {
    if (!onRegenerateVariant) return;
    const overrides: ExistingItem[] = existingItems.map((p) => ({
      name: p.name,
      decision: userDecisions[p.name] ?? p.decision,
      reasoning: p.reasoning,
    }));
    picksInFlightRef.current = true;
    void Promise.resolve(onRegenerateVariant(activeVariantId, { existingItems: overrides })).finally(() => {
      picksInFlightRef.current = false;
    });
  };

  const { savedItems, addItem, removeItem, isItemSaved, clearAll } = useSavedItems();

  // Value-of-saving nudge banner above shop grid — hidden once user saves anything or dismisses.
  const [nudgeDismissed, setNudgeDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem("saves_nudge_dismissed_v1") === "1"; } catch { return false; }
  });
  const showSavesNudge = !nudgeDismissed && savedItems.length === 0;
  const dismissNudge = () => {
    setNudgeDismissed(true);
    try { localStorage.setItem("saves_nudge_dismissed_v1", "1"); } catch {}
    track("save_nudge_dismissed", { design_id: designId ?? undefined });
  };

  // Post-View save prompt state
  const [viewPromptProduct, setViewPromptProduct] = useState<Product | null>(null);
  const promptedProductsRef = useRef<Set<string>>(new Set());
  const { promoEligible } = useSubscription();
  const genQuota = useGenerationQuota();
  const paywallFiredRef = useRef(false);
  const { refinements, submitting: refining, submit: submitRefinement, used: refineUsed, limit: refineLimit } = useRefinements(designId ?? undefined);
  useEffect(() => {
    if (isPro || paywallFiredRef.current || genQuota.loading || genQuota.unlimited || !refineLimit) return;
    const limit = genQuota.remaining <= 0 ? "designs" : refineUsed >= refineLimit ? "refines" : null;
    if (!limit) return;
    paywallFiredRef.current = true;
    track("paywall_hit", { limit, placement: "results", promo_eligible: promoEligible, design_id: designId ?? undefined });
  }, [isPro, genQuota.loading, genQuota.unlimited, genQuota.remaining, refineUsed, refineLimit, promoEligible, designId]);
  // Structural evals (user-facing) and quality evals (Huelo team analytics,
  // written by the batch job) are kept in separate maps so a late quality row
  // can never overwrite the score the user sees.
  const [evaluations, setEvaluations] = useState<Record<string, Evaluation>>({});
  const [qualityEvals, setQualityEvals] = useState<Record<string, Evaluation>>({});
  const evaluation = evaluations[activeVariantId] ?? null;
  const qualityEvaluation = qualityEvals[activeVariantId] ?? null;
  

  const isMobile = useIsMobile();
  const [regenerateCount, setRegenerateCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const prevRegeneratingRef = useRef<string | null>(null);
  useEffect(() => {
    // Count transitions from null → some id as a regenerate action.
    if (regeneratingVariantId && !prevRegeneratingRef.current) {
      setRegenerateCount((c) => c + 1);
    }
    prevRegeneratingRef.current = regeneratingVariantId ?? null;
  }, [regeneratingVariantId]);

  const [currentProducts, setCurrentProducts] = useState<Product[] | null>(products);
  const [shufflesUsed, setShufflesUsed] = useState(0);
  const [shuffling, setShuffling] = useState(false);

  const { trigger: npsTrigger, onProductClick: onNpsProductClick } = useNpsTriggers({
    enabled: isMobile,
    designId,
    savedCount: savedItems.length,
    refinementCount: refinements.length,
    regenerateCount,
    shufflesUsed,
    errorCount,
  });
  const [seenTitles, setSeenTitles] = useState<Set<string>>(
    () => new Set((products ?? []).map((p) => p.title?.toLowerCase().trim()).filter(Boolean) as string[])
  );
  // Sync local product state when the async match-design-products call
  // resolves and the parent updates the `products` prop.
  useEffect(() => {
    setCurrentProducts(products);
    if (products && products.length > 0) {
      setSeenTitles(new Set(products.map((p) => p.title?.toLowerCase().trim()).filter(Boolean) as string[]));
    }
  }, [products]);

  // Preload all product thumbnails so switching category tabs is instant.
  useEffect(() => {
    if (!currentProducts) return;
    currentProducts.forEach((p) => {
      if (p?.thumbnail) {
        const img = new Image();
        img.src = p.thumbnail;
      }
    });
  }, [currentProducts]);

  // Silent top-up: ensure at least 4 thumbnail-bearing products show on first
  // render. Replaces only the slots that came back without a usable thumbnail
  // (so visible cards are never disturbed). Capped at 2 attempts per session
  // so we never loop against the shuffle rate limit.
  const MIN_VISIBLE_PRODUCTS = 6;
  const topUpAttemptsRef = useRef(0);
  const topUpInFlightRef = useRef(false);
  useEffect(() => {
    if (!currentProducts || currentProducts.length === 0) return;
    if (furnitureItems.length === 0) return;
    if (topUpInFlightRef.current) return;
    if (topUpAttemptsRef.current >= 2) return;

    const target = Math.min(MIN_VISIBLE_PRODUCTS, furnitureItems.length);
    const visibleCount = currentProducts.filter((p) => p?.thumbnail).length;
    if (visibleCount >= target) return;

    // Slots to (re)fill: existing entries missing a thumbnail, PLUS appended
    // slots beyond currentProducts.length up to the target count.
    const missingIndices: number[] = currentProducts
      .map((p, i) => (!p?.thumbnail ? i : -1))
      .filter((i) => i >= 0);
    const appendIndices: number[] = [];
    for (let i = currentProducts.length; i < target && i < furnitureItems.length; i++) {
      appendIndices.push(i);
    }
    const slotsToFill = [...missingIndices, ...appendIndices];
    if (slotsToFill.length === 0) return;

    // Prefer uncovered furniture (by item name from p.furniture) — falls
    // back to positional lookup for legacy responses without per-product
    // furniture links.
    const coveredItems = new Set(
      currentProducts
        .map((p) => (p?.furniture?.item ?? "").toLowerCase().trim())
        .filter(Boolean),
    );
    const uncoveredFurniture = furnitureItems.filter(
      (f) => !coveredItems.has((f.item ?? "").toLowerCase().trim()),
    );

    const items = slotsToFill
      .map((slotIdx, k) => {
        const f = uncoveredFurniture[k] ?? furnitureItems[slotIdx];
        const p = currentProducts[slotIdx];
        if (!f) return null;
        return {
          item: f.item ?? p?.title ?? "item",
          searchQuery: f.searchQuery ?? p?.title ?? "",
          description: f.description,
          locked: false,
          currentTitle: p?.title ?? null,
          currentProduct: p ?? null,
        };
      })
      .filter(Boolean) as Array<{
        item: string; searchQuery: string; description?: string;
        locked: boolean; currentTitle: string | null; currentProduct: any;
      }>;
    if (items.length === 0) return;


    topUpInFlightRef.current = true;
    topUpAttemptsRef.current += 1;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("shuffle-furniture", {
          body: { budget: answers.budget, items, excludeTitles: Array.from(seenTitles) },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        const replacements: Product[] = (data?.products || []) as Product[];
        if (replacements.length === 0) return;

        setCurrentProducts((prev) => {
          if (!prev) return prev;
          const next = [...prev];
          // Ensure array is long enough for appended slots
          const maxIdx = slotsToFill[slotsToFill.length - 1] ?? -1;
          while (next.length <= maxIdx) next.push(undefined as unknown as Product);
          slotsToFill.forEach((slotIdx, i) => {
            const rep = replacements[i];
            if (rep?.thumbnail) next[slotIdx] = rep;
          });
          return next.filter(Boolean) as Product[];
        });
        setSeenTitles((prev) => {
          const nextSet = new Set(prev);
          replacements.forEach((p) => {
            if (p?.title) nextSet.add(p.title.toLowerCase().trim());
          });
          return nextSet;
        });
      } catch (err) {
        console.warn("[DesignResults] silent top-up failed", err);
      } finally {
        topUpInFlightRef.current = false;
      }
    })();
  }, [currentProducts, furnitureItems, answers.budget, seenTitles]);


  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const { toast } = useToast();
  // Confirms a save and points to where saved pieces live (and price tracking).
  const notifySaved = (count = 1) =>
    toast({
      title: count > 1 ? `${count} pieces saved` : "Saved",
      description: "Find it in Saved furniture, where you can also track prices.",
      action: (
        <ToastAction altText="View saved furniture" asChild>
          <Link to={SAVED_PAGE_PATH}>View</Link>
        </ToastAction>
      ),
    });

  // Debug mode — email-gated; renders an extra inspection panel.
  const debugEnabled = useDebugMode();
  const debugOpen = useDebugPanel();
  const [debugSection, setDebugSection] = useState<"variant" | "planning" | "prompts" | "meta">("variant");
  const activeVariantPrompt = useMemo(
    () => debug?.variants?.find((v) => v.id === activeVariantId)?.prompt ?? "",
    [debug, activeVariantId],
  );
  const copy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast({ title: `${label} copied` }),
      () => toast({ title: `Couldn't copy ${label}`, variant: "destructive" }),
    );
  };

  // Reset & subscribe to per-variant evaluation verdicts for this generation.
  // Each variant worker writes its own row to `design_evaluations` keyed by
  // `generation_id` + `variant_id`. We keep the latest row per variant.
  useEffect(() => {
    setEvaluations({});
    setQualityEvals({});
    if (!generationId) return;

    let cancelled = false;

    const upsertEval = (row: any) => {
      if (!row?.variant_id) return;
      // Route by evaluator version: only structural (and legacy v1) rows are
      // user-facing; v2-quality is team analytics.
      const isQuality = row.evaluator_version === "v2-quality";
      const setter = isQuality ? setQualityEvals : setEvaluations;
      setter((prev) => {
        const existing = prev[row.variant_id];
        // Keep only the most recent row per variant (regen replaces older)
        if (existing && existing.created_at && row.created_at &&
            new Date(existing.created_at).getTime() > new Date(row.created_at).getTime()) {
          return prev;
        }
        return {
          ...prev,
          [row.variant_id]: {
            id: row.id,
            variant_id: row.variant_id,
            overall_status: row.overall_status,
            score: Number(row.score),
            checks: Array.isArray(row.checks) ? row.checks : [],
            created_at: row.created_at,
            model_used: row.model_used ?? null,
            evaluator_version: row.evaluator_version ?? null,
          },
        };
      });
    };

    // Replay any verdicts that already landed before we mounted/subscribed
    supabase
      .from("design_evaluations")
      .select("id, variant_id, overall_status, score, checks, created_at, model_used, evaluator_version")
      .eq("generation_id", generationId)
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        if (cancelled || !data) return;
        data.forEach(upsertEval);
      });

    const channel = supabase
      .channel(`design-eval-${generationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "design_evaluations",
          filter: `generation_id=eq.${generationId}`,
        },
        (payload) => upsertEval(payload.new as any),
      )
      .subscribe();

    // Fallback poll: realtime can miss events if the channel drops or the
    // INSERT lands during a reconnect. Poll every 8s for up to 4 minutes
    // while any variant is still missing an evaluation row, so late rows
    // (slow Gemini calls, retries) always surface in the UI.
    const startedAt = Date.now();
    const expectedIds = new Set(variants.map((v) => v.id));
    const poll = setInterval(async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > 4 * 60 * 1000) {
        clearInterval(poll);
        return;
      }
      const haveAll = Array.from(expectedIds).every((id) => evaluationsRef.current[id]);
      if (haveAll) {
        clearInterval(poll);
        return;
      }
      const { data } = await supabase
        .from("design_evaluations")
        .select("id, variant_id, overall_status, score, checks, created_at, model_used, evaluator_version")
        .eq("generation_id", generationId)
        .order("created_at", { ascending: true });
      if (!cancelled && data) data.forEach(upsertEval);
    }, 8000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, [generationId, variants.map((v) => v.id).join("|")]);

  // Keep a ref of latest evaluations so the poll above can read fresh state
  // without re-subscribing on every change.
  const evaluationsRef = useRef(evaluations);
  useEffect(() => { evaluationsRef.current = evaluations; }, [evaluations]);

  // When a variant starts regenerating, drop its stale evaluation so the box
  // disappears until the new design_evaluations row arrives via realtime.
  useEffect(() => {
    if (!regeneratingVariantId) return;
    setEvaluations((prev) => {
      if (!prev[regeneratingVariantId]) return prev;
      const next = { ...prev };
      delete next[regeneratingVariantId];
      return next;
    });
  }, [regeneratingVariantId]);



  const hasProducts = currentProducts && currentProducts.length > 0;
  const canShuffle = furnitureItems.length > 0 && hasProducts;

  // Map each displayed product to its corresponding furniture slot.
  // Prefer the per-product `furniture` field (set by match-design-products
  // since Jul 2026); fall back to positional zip against furnitureItems for
  // legacy responses/rows.
  const slotMap = useMemo(() => {
    return (currentProducts ?? []).map((p, i) => ({
      product: p,
      furniture: (p?.furniture as FurnitureItem | undefined) ?? furnitureItems[i],
    }));
  }, [currentProducts, furnitureItems]);

  // Category + price filter state (client-side only — no refetch)
  const PRICE_PRESETS: { label: string; cap: number | null }[] = [
    { label: "< $100", cap: 100 },
    { label: "< $250", cap: 250 },
    { label: "< $500", cap: 500 },
    { label: "< $1,000", cap: 1000 },
    { label: "Any", cap: null },
  ];
  const [activeCategory, setActiveCategory] = useState<FurnitureCategory | "All">("All");
  const [priceCap, setPriceCap] = useState<number | null>(null);
  const [savedOnly, setSavedOnly] = useState(false);

  // Enrich each visible product with category + numeric price.
  // Prefer the category stamped server-side by match-design-products; fall back
  // to the deterministic client resolver for legacy/cached rows.
  const enrichedProducts = useMemo(() => {
    const mapped = (currentProducts ?? [])
      .map((p, i) => {
        if (!p?.thumbnail) return null;
        // Parts, plans and accessories are never a real recommendation — hide
        // them here too so designs generated before the server-side gate get
        // cleaned up as well.
        if (isJunkListing(p.title)) return null;
        const furniture = (p?.furniture as FurnitureItem | undefined) ?? furnitureItems[i];
        const stamped = (p as { category?: string }).category;
        // Resolve locally first: the stored category on older designs was
        // stamped by earlier, less accurate rules.
        const resolved = resolveCategory(
          furniture?.item ?? p.title ?? "",
          furniture?.category,
          p.title ?? undefined,
          furniture?.searchQuery,
        );
        const category = resolved !== "Other"
          ? resolved
          : stamped && (FURNITURE_CATEGORIES as readonly string[]).includes(stamped)
            ? (stamped as FurnitureCategory)
            : resolved;
        const price = parsePriceNumber(p.price);
        const slotKey = (furniture?.item ?? category).toString().trim().toLowerCase();
        const slotCategory =
          categorizeFromQuery(furniture?.item, furniture?.searchQuery) ??
          (furniture?.category && (FURNITURE_CATEGORIES as readonly string[]).includes(furniture.category)
            ? (furniture.category as FurnitureCategory)
            : null);
        const isAwin = typeof p.link === "string" && p.link.includes("awin1.com");
        return { product: p, index: i, category, price, slotKey, slotCategory, isAwin };
      })
      .filter(Boolean) as Array<{ product: Product; index: number; category: FurnitureCategory; price: number | null; slotKey: string; slotCategory: FurnitureCategory | null; isAwin: boolean }>;

    // Drop results that drifted off the piece they were sourced for (a "dining
    // table" search returning benches). Kept per slot, and skipped when it
    // would leave a piece with almost nothing.
    const bySlot = new Map<string, typeof mapped>();
    for (const e of mapped) {
      const list = bySlot.get(e.slotKey) ?? [];
      list.push(e);
      bySlot.set(e.slotKey, list);
    }
    const keep = new Set<number>();
    for (const list of bySlot.values()) {
      const onTarget = list.filter((e) => !e.slotCategory || e.category === e.slotCategory);
      const chosen = onTarget.length >= 3 ? onTarget : list;
      for (const e of chosen) keep.add(e.index);
    }
    return mapped.filter((e) => keep.has(e.index));
  }, [currentProducts, furnitureItems]);

  // "All" tab ordering: round-robin across furniture slots so the top of the
  // list shows a mix of piece types (bed, rug, lamp...) instead of every
  // option for the first slot. Category tabs keep their grouped order.
  const CATEGORY_WEIGHT: Record<FurnitureCategory, number> = {
    Beds: 0,
    Seating: 0,
    Tables: 1,
    Storage: 2,
    Rugs: 3,
    Lighting: 4,
    Decor: 5,
    Other: 6,
  };


  const orderedProducts = useMemo(() => {
    if (activeCategory !== "All") return enrichedProducts;
    const groups = new Map<string, typeof enrichedProducts>();
    for (const e of enrichedProducts) {
      const arr = groups.get(e.slotKey);
      if (arr) arr.push(e);
      else groups.set(e.slotKey, [e]);
    }
    const sortedGroups = Array.from(groups.values()).sort((a, b) => {
      const wa = CATEGORY_WEIGHT[a[0].category] ?? 9;
      const wb = CATEGORY_WEIGHT[b[0].category] ?? 9;
      if (wa !== wb) return wa - wb;
      return a[0].index - b[0].index;
    });

    // Pass 1 — round-robin across furniture slots so one option per piece
    // (bed, rug, lamp...) comes before the second option for any piece.
    const diverse: typeof enrichedProducts = [];
    const maxLen = Math.max(0, ...sortedGroups.map((g) => g.length));
    for (let round = 0; round < maxLen; round++) {
      for (const g of sortedGroups) {
        if (g[round]) diverse.push(g[round]);
      }
    }

    // Pass 2 — give affiliate partner items reserved positions: the first
    // three cards, then every fourth card after that. Both queues keep the
    // slot-diverse order from pass 1, and each pick prefers a piece type not
    // shown yet, so the top of the list never repeats one piece.
    const partnerQueue = diverse.filter((e) => e.isAwin);
    const otherQueue = diverse.filter((e) => !e.isAwin);
    if (partnerQueue.length === 0 || otherQueue.length === 0) return diverse;

    const totalSlots = groups.size;
    const usedSlots = new Set<string>();
    const take = (queue: typeof enrichedProducts) => {
      if (queue.length === 0) return null;
      let idx = 0;
      if (usedSlots.size < totalSlots) {
        const fresh = queue.findIndex((e) => !usedSlots.has(e.slotKey));
        if (fresh >= 0) idx = fresh;
      }
      const [picked] = queue.splice(idx, 1);
      usedSlots.add(picked.slotKey);
      return picked;
    };

    const out: typeof enrichedProducts = [];
    let pos = 0;
    while (partnerQueue.length > 0 || otherQueue.length > 0) {
      const wantsPartner = pos < 3 || (pos >= 6 && (pos - 6) % 4 === 0);
      const primary = wantsPartner ? partnerQueue : otherQueue;
      const secondary = wantsPartner ? otherQueue : partnerQueue;
      const picked = take(primary) ?? take(secondary);
      if (!picked) break;
      out.push(picked);
      pos++;
    }
    return out;
  }, [enrichedProducts, activeCategory]);



  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { All: enrichedProducts.length };
    for (const c of FURNITURE_CATEGORIES) counts[c] = 0;
    for (const e of enrichedProducts) counts[e.category] = (counts[e.category] ?? 0) + 1;
    return counts;
  }, [enrichedProducts]);

  const visibleCategories = useMemo(
    () => FURNITURE_CATEGORIES.filter((c) => (categoryCounts[c] ?? 0) > 0),
    [categoryCounts],
  );

  const maxPriceForCategory = useMemo(() => {
    const pool = activeCategory === "All"
      ? enrichedProducts
      : enrichedProducts.filter((e) => e.category === activeCategory);
    const max = Math.max(0, ...pool.map((e) => e.price ?? 0));
    return Math.ceil(Math.max(max, 100) / 50) * 50;
  }, [enrichedProducts, activeCategory]);

  const filteredProducts = useMemo(() => {
    return enrichedProducts.filter((e) => {
      // Hide poorly-rated listings (unrated items are kept).
      const rating = (e.product as any)?.rating;
      const ratingOk = typeof rating !== "number" || rating >= 3;
      const catOk = activeCategory === "All" || e.category === activeCategory;
      const priceOk = priceCap == null || (e.price != null && e.price <= priceCap);
      const savedOk = !savedOnly || isItemSaved(e.product);
      return ratingOk && catOk && priceOk && savedOk;
    });
  }, [enrichedProducts, activeCategory, priceCap, savedOnly, isItemSaved]);

  const resetFilters = () => {
    setActiveCategory("All");
    setPriceCap(null);
    setSavedOnly(false);
  };

  // Reset filters when a new generation lands (products list changes fundamentally)
  const productsSignature = (currentProducts ?? []).map((p) => p?.title).join("|");
  useEffect(() => {
    setActiveCategory("All");
    setPriceCap(null);
    setSavedOnly(false);
  }, [productsSignature]);

  // Reset pagination when filter surface changes
  useEffect(() => {
    setVisibleCount(8);
  }, [productsSignature, activeCategory, priceCap, savedOnly]);



  const handleShuffle = async () => {
    if (!canShuffle || shuffling) return;
    setShuffling(true);
    try {
      const items = slotMap.map(({ product, furniture }) => {
        const locked = isItemSaved(product);
        return {
          item: furniture?.item ?? product.title,
          searchQuery: furniture?.searchQuery ?? product.title,
          description: furniture?.description,
          locked,
          currentTitle: product.title,
          currentProduct: product,
        };
      });

      const { data, error } = await supabase.functions.invoke("shuffle-furniture", {
        body: {
          budget: answers.budget,
          items,
          excludeTitles: Array.from(seenTitles),
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const newProducts: Product[] = (data.products || []) as Product[];
      setCurrentProducts(newProducts);

      // Track every title we've ever shown so we don't re-suggest it
      setSeenTitles((prev) => {
        const next = new Set(prev);
        newProducts.forEach((p) => {
          if (p?.title) next.add(p.title.toLowerCase().trim());
        });
        return next;
      });

      const nextUsed = shufflesUsed + 1;
      setShufflesUsed(nextUsed);

      if (nextUsed === FEEDBACK_AFTER_SHUFFLES) {
        setShowFeedback(true);
      }
    } catch (err: any) {
      console.error("Shuffle failed:", err);
      captureSentryException(err, { source: "shuffle" });
      setErrorCount((c) => c + 1);
      toast({
        title: "Shuffle failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setShuffling(false);
    }
  };


  const handleSendFeedback = async () => {
    const msg = feedbackText.trim();
    if (!msg) return;
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (userId) {
        await supabase.from("design_feedback").insert({
          user_id: userId,
          message: msg,
          shuffles_used: shufflesUsed,
        });
      }
      setFeedbackSent(true);
      toast({ title: "Thanks for the feedback", description: "We'll use this to make better suggestions." });
    } catch (err: any) {
      console.error("Feedback submission failed:", err);
      captureSentryException(err, { source: "feedback-submit" });
      toast({
        title: "Couldn't send feedback",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-4xl mx-auto space-y-6"
    >
      <div className="text-left space-y-2">
        <h1 className="text-2xl md:text-4xl font-display text-foreground" style={{ letterSpacing: "-0.72px" }}>
          Your {answers.style} space
        </h1>
        {answers.purposes?.length > 0 && (
          <p className="text-muted-foreground text-sm">
            For {answers.purposes.join(", ").toLowerCase()}
          </p>
        )}
      </div>

      {/* Debug panel toggle lives in the sidebar — see AppSidebar */}


      {/* Structural QA verdict renders as a compact row under the image */}


      {/* Room Preview with Before/After + variation switcher — sticky on mobile so the image stays visible */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-3"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] sm:text-xs font-semibold tracking-normal text-foreground border border-border rounded-full bg-muted/40">
              {activeVariant.label}
            </span>
            {designId && (
              refineUsed >= refineLimit ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold tracking-normal text-muted-foreground border border-border bg-muted/40">
                  <Sparkles className="w-3 h-3" /> Refinements used
                </span>
              ) : isMobile ? (
                <button
                  type="button"
                  onClick={() => {
                    setRefineOpen(true);
                    track("refine_sheet_opened", { design_id: designId, variant_id: activeVariantId });
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold tracking-normal bg-gradient-cta text-primary-foreground shadow-elegant hover:opacity-90 transition-opacity"
                >
                  <Sparkles className="w-3 h-3" /> Refine · {refineLimit - refineUsed} left
                </button>
              ) : (
                <Popover
                  open={refineOpen}
                  onOpenChange={(o) => {
                    setRefineOpen(o);
                    if (o) track("refine_sheet_opened", { design_id: designId, variant_id: activeVariantId });
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold tracking-normal bg-gradient-cta text-primary-foreground shadow-elegant hover:opacity-90 transition-opacity"
                    >
                      <Sparkles className="w-3 h-3" /> Refine · {refineLimit - refineUsed} left
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" sideOffset={8} className="w-[400px] p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <h3 className="font-display text-sm text-foreground flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-primary" />
                        Refine this design
                      </h3>
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {refineLimit - refineUsed} of {refineLimit} left
                      </span>
                    </div>
                    <RefinePanel
                      compact
                      designId={designId}
                      used={refineUsed}
                      limit={refineLimit}
                      isSubmitting={refining}
                      onSuccess={() => setRefineOpen(false)}
                      onSubmit={async (params) => {
                        const submittedForVariantId = activeVariantId;
                        onUserAction?.("refinement_submitted");
                        const r = await submitRefinement({
                          ...params,
                          baseImageUrl: activeVariant.image,
                          variantId: submittedForVariantId,
                        });
                        if (r && activeVariantId === submittedForVariantId) setActiveRefinement(r);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )
            )}

          </div>

          <button
            onClick={() => setShowBefore(!showBefore)}
            className="hidden sm:inline-flex shrink-0 px-3 py-1.5 rounded-full border border-border text-foreground text-[12px] font-semibold tracking-normal hover:bg-foreground/5 transition-colors"
          >
            {showBefore ? "Show After" : "Show Before"}
          </button>
        </div>

        <div className="relative overflow-hidden rounded-lg border border-border shadow-lg group">
          <button
            type="button"
            onClick={() => {
              setLightboxOpen(true);
              track("design_image_expanded", { designId, variantId: activeVariantId, view: showBefore ? "before" : "after" });
            }}
            aria-label="View full-size image"
            className="block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <img
              src={showBefore ? preview : displayedAiImage}
              alt={showBefore ? "Original room" : `Your redesigned room — ${activeVariant.label}`}
              className="w-full aspect-video object-cover"
            />
          </button>
          <div className="pointer-events-none absolute top-3 right-3 hidden sm:flex items-center gap-1 rounded-full px-2 py-1 bg-background/80 backdrop-blur-sm border border-border text-foreground text-[11px] font-semibold tracking-normal opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize2 className="w-3 h-3" /> Expand
          </div>
          {regeneratingVariantId === activeVariantId && !showBefore && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm pointer-events-none">
              <div className="flex items-center gap-2 text-sm text-foreground font-semibold">
                <Loader2 className="w-4 h-4 animate-spin" />
                Regenerating {activeVariant.label}…
              </div>
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setShowBefore(!showBefore); }}
            className="sm:hidden absolute bottom-3 right-3 rounded-full px-3 py-1.5 border border-border text-foreground text-[11px] font-semibold tracking-normal bg-background/80 backdrop-blur-sm hover:bg-background transition-colors"
          >
            {showBefore ? "Show After" : "Show Before"}
          </button>
        </div>
        {/* Structural evaluation still runs server-side for our records, but is
            no longer surfaced to users. Team members can inspect it in the debug panel. */}

      </motion.div>

      {/* Pro upsell once the user has hit a limit (designs or refines). */}
      {UPSELLS_ENABLED && !isPro && !genQuota.loading && !genQuota.unlimited &&
        genQuota.remaining > 0 && refineUsed >= refineLimit && (
          <>
            <UpsellDialog type="refine_limit" active promoEligible={promoEligible} />
            <TrackOnView event="upsell_viewed" properties={{ placement: "refine_limit", format: "inline", promo_eligible: promoEligible, plan: "free" }}>
              <div className="flex items-center justify-between gap-3 rounded-full border border-border bg-muted/60 px-4 py-2 text-xs">
                <span className="text-muted-foreground">
                  You're out of refinements this month.{" "}
                  <span className="text-foreground font-medium">
                    {promoEligible ? `Pro gives you 15 a month — $${PROMO.price} first month.` : "Pro gives you 15 a month."}
                  </span>
                </span>
                <Link
                  to="/pricing?plan=pro&billing=monthly"
                  onClick={() => track("upsell_clicked", { placement: "refine_limit", format: "inline", promo_eligible: promoEligible, plan: "free" })}
                  className="shrink-0 font-semibold text-primary hover:underline"
                >
                  Go Pro
                </Link>
              </div>
            </TrackOnView>
          </>
        )}

      {/* Refinement thumbnails — directly under the hero image */}
      {designId && (
        <RefinementsStrip
          compact
          designId={designId}
          refinements={refinements.filter((r) => (r.variant_id ?? variants[0].id) === activeVariantId)}
          isSubmitting={refining}
          activeId={refinementMatchesActive ? activeRefinement!.id : null}
          originalImage={activeVariant.image}
          onSelectOriginal={() => setActiveRefinement(null)}
          onSelect={(r) => setActiveRefinement(r)}
        />
      )}

      {/* Refine sheet — mobile only; desktop uses the popover anchored to the Refine pill */}
      {designId && isMobile && (
        <Sheet open={refineOpen} onOpenChange={setRefineOpen}>
          <SheetContent
            side="bottom"
            overlayClassName="backdrop-blur-none bg-[hsl(var(--foreground)/0.15)]"
            className="max-h-[80vh] overflow-y-auto sm:max-w-md"
          >
            <SheetHeader className="text-left">
              <div className="flex items-center gap-3 pr-8">
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className="shrink-0 w-12 h-12 overflow-hidden border border-border"
                  aria-label="View design full size"
                >
                  <img src={displayedAiImage} alt="Design being refined" className="w-full h-full object-cover" />
                </button>
                <div className="min-w-0">
                  <SheetTitle className="flex items-center gap-1.5 font-display">
                    <Sparkles className="w-4 h-4 text-primary" />
                    Refine this design
                  </SheetTitle>
                  <SheetDescription>
                    {Math.max(0, refineLimit - refineUsed)} of {refineLimit} iteration tokens left this month.
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>
            <div className="mt-4">

              <RefinePanel
                compact
                designId={designId}
                used={refineUsed}
                limit={refineLimit}
                isSubmitting={refining}
                onSuccess={() => setRefineOpen(false)}
                onSubmit={async (params) => {
                  const submittedForVariantId = activeVariantId;
                  onUserAction?.("refinement_submitted");
                  const r = await submitRefinement({
                    ...params,
                    baseImageUrl: activeVariant.image,
                    variantId: submittedForVariantId,
                  });
                  if (r && activeVariantId === submittedForVariantId) setActiveRefinement(r);
                }}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] w-auto p-0 bg-transparent border-0 shadow-none">
          <VisuallyHidden>
            <DialogTitle>{showBefore ? "Original room" : `Redesigned room — ${activeVariant.label}`}</DialogTitle>
          </VisuallyHidden>
          <img
            src={showBefore ? preview : displayedAiImage}
            alt={showBefore ? "Original room" : `Your redesigned room — ${activeVariant.label}`}
            className="w-auto max-w-[95vw] max-h-[95vh] object-contain mx-auto"
          />
        </DialogContent>
      </Dialog>


      {/* Tab navigation — details on demand under the hero image */}
      <div className="-mx-5 px-5 sm:mx-0 sm:px-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] border-b border-border">
        <div className="inline-flex items-center gap-1 min-w-full sm:min-w-0" role="tablist">
          {(() => {
            const tabs: { id: TabId; label: string; badge?: string | number }[] = [
              { id: "shop", label: "Shop" },
              ...(existingItems.length > 0 ? [{ id: "pieces" as TabId, label: "My pieces" }] : []),
              { id: "variations", label: "More variations" },
            ];
            return tabs.map((t) => {
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => {
                    if (activeTab === t.id) return;
                    setActiveTab(t.id);
                    track("results_tab_changed", { tab: t.id, design_id: designId ?? null });
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold tracking-normal whitespace-nowrap shrink-0 border-b-2 transition-colors ${
                    isActive
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                  {t.badge != null && (
                    <span className={`hidden sm:inline text-[10px] font-normal ${isActive ? "opacity-80" : "opacity-60"}`}>
                      {t.badge}
                    </span>
                  )}
                </button>
              );
            });
          })()}
        </div>
      </div>

      {/* Shop tab — furniture suggestions */}
      {activeTab === "shop" && (<>
      {loadingProducts && !hasProducts && (
        <div className="space-y-4">
          <div className="flex items-start gap-2">
            <ShoppingBag className="w-5 h-5 text-foreground shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg md:text-2xl font-display text-foreground">
                Shop Similar Pieces
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Finding matching furniture for your design…
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="flex gap-4 p-4 bg-card border border-border animate-pulse"
              >
                <div className="w-24 h-24 sm:w-32 sm:h-32 bg-muted shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-3.5 bg-muted w-3/4" />
                  <div className="h-3.5 bg-muted w-1/2" />
                  <div className="h-5 bg-muted w-1/3 mt-3" />
                  <div className="flex gap-2 mt-3">
                    <div className="h-6 bg-muted w-16" />
                    <div className="h-6 bg-muted w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasProducts && (
        <div className="space-y-4">
          {/* Desktop header — hidden on mobile since the active tab already says "Shop" */}
          <div className="hidden sm:flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-foreground shrink-0" />
                <h2 className="text-lg md:text-2xl font-display text-foreground">
                  Shop Similar Pieces
                </h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 text-left">
                These are similar items inspired by your design — not exact matches to the rendered image.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start sm:self-auto">
              {canShuffle && (
                <button
                  onClick={handleShuffle}
                  disabled={shuffling}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold tracking-normal text-foreground border border-border hover:border-foreground/30 transition-colors disabled:opacity-60"
                >
                  {shuffling ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Shuffle className="w-3.5 h-3.5" />
                  )}
                  Shuffle
                </button>
              )}
              {savedItems.length > 0 && (
                <button
                  onClick={() => {
                    const next = !savedOnly;
                    setSavedOnly(next);
                    debouncedFilterTrack({
                      filter_type: "saved_only",
                      filter_value: next,
                      category: activeCategory,
                      price_cap: priceCap,
                    });
                  }}
                  aria-pressed={savedOnly}
                  className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold tracking-normal border transition-colors ${
                    savedOnly
                      ? "bg-foreground text-background border-foreground"
                      : "text-foreground border-border hover:border-foreground/30"
                  }`}
                >
                  <Heart className="w-3.5 h-3.5 fill-current" />
                  Saved ({savedItems.length})
                </button>
              )}

            </div>
          </div>

          {/* Mobile-only compact filter bar — one row with category + price popovers and icon actions */}
          <div className="sm:hidden flex items-center gap-2">
            <Popover>
              <PopoverTrigger className="flex-1 min-w-0 inline-flex items-center justify-between gap-1.5 px-3 py-2 text-xs font-semibold tracking-normal text-foreground border border-border rounded-full">
                <span className="truncate">
                  {activeCategory}
                </span>

                <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
              </PopoverTrigger>
              <PopoverContent align="start" className="p-1.5 w-56 max-h-72 overflow-y-auto">
                {(["All", ...visibleCategories] as const).map((cat) => {
                  const isActive = activeCategory === cat;

                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        if (activeCategory === cat) return;
                        setActiveCategory(cat as FurnitureCategory | "All");
                        debouncedFilterTrack({
                          filter_type: "category",
                          filter_value: cat,
                          category: cat,
                          price_cap: priceCap,
                        });
                      }}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold tracking-normal rounded-md transition-colors ${
                        isActive ? "bg-selected text-selected-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      <span>{cat}</span>
                    </button>

                  );
                })}
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold tracking-normal text-foreground border border-border rounded-full whitespace-nowrap">
                {priceCap == null ? "Any $" : `< $${priceCap}`}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </PopoverTrigger>
              <PopoverContent align="end" className="p-1.5 w-44">
                {PRICE_PRESETS.map((p) => {
                  const isActive = priceCap === p.cap;
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        if (priceCap === p.cap) return;
                        setPriceCap(p.cap);
                        debouncedFilterTrack({
                          filter_type: "price_cap",
                          filter_value: p.cap,
                          category: activeCategory,
                          price_cap: p.cap,
                        });
                      }}
                      className={`w-full text-left px-3 py-2 text-xs font-semibold tracking-normal rounded-md transition-colors ${
                        isActive ? "bg-selected text-selected-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </PopoverContent>
            </Popover>
            {canShuffle && (
              <button
                onClick={handleShuffle}
                disabled={shuffling}
                aria-label="Shuffle suggestions"
                className="inline-flex items-center justify-center p-2 text-foreground border border-border rounded-full disabled:opacity-60"
              >
                {shuffling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
              </button>
            )}
            {savedItems.length > 0 && (
              <button
                onClick={() => {
                  const next = !savedOnly;
                  setSavedOnly(next);
                  debouncedFilterTrack({
                    filter_type: "saved_only",
                    filter_value: next,
                    category: activeCategory,
                    price_cap: priceCap,
                  });
                }}
                aria-pressed={savedOnly}
                aria-label={savedOnly ? "Show all pieces" : `Show only saved pieces (${savedItems.length})`}
                className={`relative inline-flex items-center justify-center p-2 border rounded-full transition-colors ${
                  savedOnly ? "bg-foreground text-background border-foreground" : "text-foreground border-border"
                }`}
              >
                <Heart className="w-4 h-4 fill-current" />
                <span
                  className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-semibold flex items-center justify-center ${
                    savedOnly ? "bg-background text-foreground" : "bg-foreground text-background"
                  }`}
                >
                  {savedItems.length}
                </span>
              </button>
            )}

          </div>

          {/* Desktop category strip */}
          <div className="hidden sm:block -mx-5 px-5 sm:mx-0 sm:px-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <div className="inline-flex items-center gap-1.5 min-w-full sm:min-w-0">
              {(["All", ...visibleCategories] as const).map((cat) => {
                const isActive = activeCategory === cat;

                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      if (activeCategory === cat) return;
                      setActiveCategory(cat as FurnitureCategory | "All");
                      debouncedFilterTrack({
                        filter_type: "category",
                        filter_value: cat,
                        category: cat,
                        price_cap: priceCap,
                      });
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold tracking-normal rounded-full whitespace-nowrap shrink-0 border transition-colors ${
                      isActive
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/30"
                    }`}
                    aria-pressed={isActive}
                  >
                    {cat}
                  </button>

                );
              })}
            </div>
          </div>

          {/* Desktop price filter */}
          <div className="hidden sm:flex flex-col gap-3 p-3 sm:p-4 border border-border bg-muted/30">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold tracking-normal text-muted-foreground">
                Max price
              </span>
              <span className="text-xs font-semibold text-foreground">
                {priceCap == null ? "Any" : `< $${priceCap.toLocaleString()}`}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRICE_PRESETS.map((p) => {
                const isActive = priceCap === p.cap;
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => {
                      if (priceCap === p.cap) return;
                      setPriceCap(p.cap);
                      debouncedFilterTrack({
                        filter_type: "price_cap",
                        filter_value: p.cap,
                        category: activeCategory,
                        price_cap: p.cap,
                      });
                    }}
                    className={`px-3 py-1 text-[11px] font-semibold tracking-normal rounded-full border transition-colors ${
                      isActive
                        ? "bg-foreground text-background border-foreground"
                        : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/30"
                    }`}
                    aria-pressed={isActive}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="pt-1">
              <Slider
                value={[priceCap ?? maxPriceForCategory]}
                max={Math.max(maxPriceForCategory, 100)}
                min={0}
                step={25}
                onValueChange={(vals) => {
                  const v = vals[0];
                  setPriceCap(v >= maxPriceForCategory ? null : v);
                }}
                onValueCommit={(vals) => {
                  const cap = vals[0] >= maxPriceForCategory ? null : vals[0];
                  debouncedFilterTrack({
                    filter_type: "price_cap",
                    filter_value: cap,
                    category: activeCategory,
                    price_cap: cap,
                  });
                }}
                aria-label="Maximum price"
              />
            </div>
          </div>


          <div className="flex items-start gap-3 p-3 sm:p-4 bg-accent/30 border border-accent rounded-lg">
            <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0 mt-0.5 fill-current" />
            <p className="flex-1 text-xs sm:text-sm text-foreground leading-snug">
              <span className="font-semibold">Tap the heart</span> to save pieces to your room list. Come back anytime to review or buy them.
            </p>
          </div>


          {filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 p-8 border border-dashed border-border text-center">
              <p className="text-sm text-muted-foreground">
                No items in this range — try adjusting your filter
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold tracking-normal text-foreground border border-border hover:border-foreground/30 transition-colors rounded-full"
              >
                Reset filters
              </button>
            </div>
          )}
          {/* All cards stay mounted — filter with CSS so images stay decoded */}
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3">
            {(() => {
              let visibleIdx = 0;
              return orderedProducts.map(({ product, index: i, category, price }) => {
                const catOk = activeCategory === "All" || category === activeCategory;
                const priceOk = priceCap == null || (price != null && price <= priceCap);
                const passesFilter = catOk && priceOk;
                const posInFiltered = passesFilter ? visibleIdx++ : -1;
                const show = passesFilter && posInFiltered < visibleCount;
                const saved = isItemSaved(product);
                const itemId = productItemId(product);
                const itemPrice = parsePriceNumber(product.price);
                const itemProps = {
                  item_id: itemId,
                  item_price: itemPrice,
                  item_brand: product.source,
                  position_in_list: i,
                };
                const trackFurnitureClick = () => {
                  track("furniture_item_viewed", { ...itemProps, trigger: "click" });
                  void trackBrevo("furniture_click", {
                    item_id: itemId,
                    item_brand: product.source,
                    design_id: designId ?? undefined,
                  });
                  touchActivity();
                };
                const isHighlyRated = typeof product.rating === "number" && product.rating >= 4.3;
                const isAffiliate =
                  typeof product.link === "string" && product.link.includes("awin1.com");
                return (
                <TrackOnView
                  key={`${product.title}-${i}`}
                  event="furniture_item_impressed"
                  properties={{ ...itemProps, category }}
                  className={show ? undefined : "hidden"}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02, duration: 0.15 }}
                    className="group relative flex flex-col bg-card border border-border rounded-lg overflow-hidden hover:border-foreground/30 hover:shadow-elegant transition-all duration-300"
                  >
                    {/* Image */}
                    <div className="relative w-full aspect-square bg-muted">
                      <img
                        src={product.thumbnail}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />

                      {/* Top-left badges */}
                      <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
                        {saved && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-normal bg-foreground text-background rounded-full"
                            title="Saved — won't shuffle"
                          >
                            <Lock className="w-2.5 h-2.5" />
                            Saved
                          </span>
                        )}
                        {isHighlyRated && !saved && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-normal bg-background/95 text-foreground border border-border rounded-full">
                            <Star className="w-2.5 h-2.5 fill-current" />
                            Highly rated
                          </span>
                        )}
                        {isAffiliate && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-normal bg-background/95 text-muted-foreground border border-border rounded-full"
                            title="Partner product — we may earn a small commission, at no extra cost to you"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            Partner
                          </span>
                        )}
                      </div>

                    </div>

                    {/* Body */}
                    <div className="flex flex-col flex-1 p-3 sm:p-4 gap-1.5">
                      <p className="text-base sm:text-lg font-semibold text-foreground">
                        {product.price}
                      </p>
                      <h3 className="font-medium text-foreground text-sm line-clamp-2 leading-snug">
                        {product.title}
                      </h3>
                      {typeof product.rating === "number" && product.rating > 0 && (
                        <div>
                          <StarRating rating={product.rating} reviews={product.reviews} />
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-2 mt-auto pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (saved) {
                              removeItem(product, { designId: designId ?? null });
                            } else {
                              addItem(product, { designId: designId ?? null, source: "card" });
                              notifySaved();
                            }
                          }}
                          aria-label={saved ? "Remove from saved" : "Save item"}
                          className={`flex-1 min-w-[7rem] inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full tracking-normal transition-opacity hover:opacity-90 ${
                            saved
                              ? "bg-selected text-selected-foreground"
                              : "bg-primary-strong text-primary-strong-foreground"
                          }`}
                        >
                          <Heart className={`w-3.5 h-3.5 ${saved ? "fill-current" : ""}`} />
                          {saved ? "Saved" : "Save"}
                        </button>
                        {product.link && (
                          <a
                            href={product.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              trackFurnitureClick();
                              track("outbound_link_clicked", {
                                retailer: product.source,
                                item_id: itemId,
                                item_price: itemPrice,
                                category,
                                link_type: "view",
                                source: "design_results",
                                ...affiliateProps(product.link),
                              });
                              onNpsProductClick();
                              // Post-view save prompt (only if unsaved and not already prompted)
                              if (!saved && !promptedProductsRef.current.has(itemId)) {
                                promptedProductsRef.current.add(itemId);
                                window.setTimeout(() => {
                                  setViewPromptProduct(product);
                                  track("save_prompt_shown", {
                                    item_id: itemId,
                                    source: "view_click_prompt",
                                    design_id: designId ?? undefined,
                                  });
                                }, 700);
                              }
                            }}
                            className="flex-1 min-w-[7rem] inline-flex items-center justify-center gap-1 text-xs font-semibold text-foreground bg-background border border-border px-3 py-2 rounded-full tracking-normal hover:bg-muted transition-colors"
                          >
                            Shop <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>

                    </div>
                  </motion.div>
                </TrackOnView>
                );
              });
            })()}
          </div>

          {filteredProducts.length > visibleCount && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => {
                  track("products_load_more_clicked", {
                    category: activeCategory,
                    price_cap: priceCap,
                    shown_before: visibleCount,
                  });
                  setVisibleCount((n) => n + 15);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold tracking-normal text-foreground border border-border hover:border-foreground/30 transition-colors rounded-full"
              >
                Load more
              </button>
            </div>
          )}

          <p className="pt-2 text-[11px] leading-relaxed text-muted-foreground text-center">
            Some links are affiliate links. If you buy through them we may earn a small commission, at no extra cost to you.
          </p>


          {showFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 border border-border bg-muted/40 space-y-3"
            >
              {feedbackSent ? (
                <p className="text-sm text-foreground">
                  Thanks — we've logged your feedback and we'll keep tuning the matches.
                </p>
              ) : (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Still not seeing the right pieces?</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Tell us what's off — wrong style, wrong scale, prices too high, anything. We read every note.
                    </p>
                  </div>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="What's not working for you?"
                    className="w-full p-3 bg-background border border-border text-sm text-foreground focus:outline-none focus:border-foreground/40"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleSendFeedback}
                      disabled={!feedbackText.trim()}
                      className="px-4 py-2 text-xs font-semibold tracking-normal bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      Send feedback
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </div>
      )}
      </>)}


      {/* Debug panel — inspect prompts, planning, evaluation, pipeline metadata */}
      {debugEnabled && debugOpen && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-dashed border-foreground/30 bg-muted/40 p-4 space-y-4 text-sm"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 font-semibold text-foreground">
              <Bug className="w-4 h-4" /> Debug — {activeVariant.label}
            </div>
            <div className="inline-flex items-center gap-1 p-0.5 border border-border rounded-full bg-background">
              {([
                { id: "variant", label: "Variant" },
                { id: "planning", label: "Planning" },
                { id: "prompts", label: "Prompts" },
                { id: "meta", label: "Pipeline" },
              ] as const).map((s) => (
                <button
                  key={s.id}
                  onClick={() => setDebugSection(s.id)}
                  className={`px-3 py-1 text-[10px] font-semibold tracking-normal rounded-full transition-colors ${
                    debugSection === s.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {debugSection === "variant" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold tracking-normal text-muted-foreground">
                  Final prompt sent to Gemini ({activeVariant.label})
                </p>
                <button
                  onClick={() => copy(activeVariantPrompt, "Prompt")}
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed p-3 bg-background border border-border max-h-72 overflow-auto">
                {activeVariantPrompt || "(no prompt captured — generation likely fell back to the default prompt)"}
              </pre>

              {([
                { title: "Structural (user-facing)", ev: evaluation, empty: "No structural evaluation row yet for this variant." },
                { title: "Quality (team only, batched)", ev: qualityEvaluation, empty: "No quality evaluation yet — pending batch." },
              ] as const).map(({ title, ev, empty }) => (
                <div key={title} className="space-y-2">
                  <p className="text-xs font-semibold tracking-normal text-muted-foreground">
                    {title}
                    {ev
                      ? ` — ${ev.overall_status.toUpperCase()} · score ${Math.round(ev.score * 100)}%${ev.checks[0]?.model ? ` · ${ev.checks[0].model}` : ""}`
                      : ""}
                  </p>
                  {!ev && <p className="text-xs text-muted-foreground">{empty}</p>}
                  {ev && (
                    <div className="border border-border bg-background">
                      <table className="w-full text-[11px]">
                        <thead className="text-muted-foreground">
                          <tr className="border-b border-border">
                            <th className="text-left p-2 font-semibold">Check</th>
                            <th className="text-left p-2 font-semibold w-16">Status</th>
                            <th className="text-left p-2 font-semibold w-14">Score</th>
                            <th className="text-left p-2 font-semibold">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ev.checks.map((c) => (
                            <tr key={c.name} className="border-b border-border last:border-0">
                              <td className="p-2 font-mono">{c.name}</td>
                              <td className={`p-2 font-semibold ${
                                c.status === "pass" ? "text-success" : c.status === "warn" ? "text-warning" : "text-destructive"
                              }`}>{c.status}</td>
                              <td className="p-2 font-mono">{c.score.toFixed(2)}</td>
                              <td className="p-2 text-muted-foreground">{c.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {debugSection === "planning" && (
            <div className="space-y-3">
              {debug?.room_size && (
                <div>
                  <p className="text-xs font-semibold tracking-normal text-muted-foreground mb-1">Room size</p>
                  <p className="text-[11px] font-mono">
                    {debug.room_size.sqm
                      ? `~${Math.round(debug.room_size.sqm)} sq m (~${Math.round(debug.room_size.sqm * 10.7639)} sq ft) · ${
                          debug.room_size.source === "estimated" ? "estimated from photo" : "provided by user"
                        }${debug.room_size.confidence ? ` · ${debug.room_size.confidence} confidence` : ""}`
                      : "unknown"}
                  </p>
                  {debug.room_size.basis && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">Basis: {debug.room_size.basis}</p>
                  )}
                </div>
              )}
              {debug?.anchors && debug.anchors.length > 0 && (

                <div>
                  <p className="text-xs font-semibold tracking-normal text-muted-foreground mb-1">Parsed anchors</p>
                  <div className="flex flex-wrap gap-1.5">
                    {debug.anchors.map((a) => (
                      <span key={a} className="text-[11px] px-2 py-0.5 bg-background border border-border">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              <details open className="border border-border bg-background">
                <summary className="cursor-pointer p-2 text-xs font-semibold tracking-normal text-muted-foreground inline-flex items-center gap-1">
                  <ChevronDown className="w-3 h-3" /> Step 1 — Spatial analysis
                </summary>
                <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed p-3 border-t border-border max-h-72 overflow-auto">
                  {debug?.step1 || "(not captured)"}
                </pre>
              </details>
              <details className="border border-border bg-background">
                <summary className="cursor-pointer p-2 text-xs font-semibold tracking-normal text-muted-foreground inline-flex items-center gap-1">
                  <ChevronDown className="w-3 h-3" /> Step 2 — Furnishing strategy
                </summary>
                <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed p-3 border-t border-border max-h-72 overflow-auto">
                  {debug?.step2 || "(not captured)"}
                </pre>
              </details>
            </div>
          )}

          {debugSection === "prompts" && (
            generationId ? (
              <DesignDebugPanel generationId={generationId} />
            ) : (
              <p className="text-xs text-muted-foreground">No generation id for this design.</p>
            )
          )}

          {debugSection === "meta" && (
            <div className="space-y-2 text-[12px]">
              <dl className="grid grid-cols-[160px_1fr] gap-y-1 gap-x-4">
                <dt className="text-muted-foreground">generation_id</dt>
                <dd className="font-mono break-all">{generationId ?? "—"}</dd>
                <dt className="text-muted-foreground">variant_id</dt>
                <dd className="font-mono">{activeVariantId}</dd>
                <dt className="text-muted-foreground">design_id</dt>
                <dd className="font-mono break-all">{designId ?? "—"}</dd>
                <dt className="text-muted-foreground">structural_eval_id</dt>
                <dd className="font-mono break-all">{evaluation?.id ?? "—"}</dd>
                <dt className="text-muted-foreground">structural_evaluated_at</dt>
                <dd className="font-mono">{evaluation?.created_at ?? "—"}</dd>
                <dt className="text-muted-foreground">structural version</dt>
                <dd className="font-mono">{evaluation?.evaluator_version ?? "—"}</dd>
                <dt className="text-muted-foreground">quality_eval_id</dt>
                <dd className="font-mono break-all">{qualityEvaluation?.id ?? "— (pending batch)"}</dd>
                <dt className="text-muted-foreground">quality_evaluated_at</dt>
                <dd className="font-mono">{qualityEvaluation?.created_at ?? "— (pending batch)"}</dd>
                <dt className="text-muted-foreground">quality version</dt>
                <dd className="font-mono">{qualityEvaluation?.evaluator_version ?? "—"}</dd>
                <dt className="text-muted-foreground">image_url</dt>
                <dd className="font-mono break-all">
                  {displayedAiImage?.startsWith("http") ? (
                    <a href={displayedAiImage} target="_blank" rel="noreferrer" className="underline">{displayedAiImage}</a>
                  ) : "(inline data URL)"}
                </dd>
                <dt className="text-muted-foreground">image model</dt>
                <dd className="font-mono">{evaluation?.model_used ?? qualityEvaluation?.model_used ?? "— (pending evaluation)"}</dd>
                <dt className="text-muted-foreground">variants rendered</dt>
                <dd className="font-mono">{variants.length}</dd>
              </dl>
              <p className="text-[11px] text-muted-foreground">
                Per-call models and latency live in the Prompts tab.
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* My pieces tab — existing furniture decisions */}
      {activeTab === "pieces" && existingItems.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-secondary" />
            <h2 className="text-sm font-semibold text-foreground">Your Existing Pieces</h2>
            <span className="text-xs text-muted-foreground">({existingItems.length})</span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Huelo spotted these in your photo and weighed each against your {answers.style?.toLowerCase() || "chosen"} goals. Override any pick, then regenerate.
          </p>

          <div className="border border-border divide-y divide-border bg-card">
            {existingItems.map((item) => {
              const current = userDecisions[item.name] ?? item.decision;
              const aiPick = item.decision;
              const changed = current !== aiPick;
              const isOpen = expandedItem === item.name;
              return (
                <div key={item.name} className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setExpandedItem(isOpen ? null : item.name)}
                      className="flex-1 flex items-center gap-2 text-left min-w-0"
                      aria-expanded={isOpen}
                    >
                      <ChevronDown
                        className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                      <span className="text-sm font-medium text-foreground truncate">
                        {item.name}
                      </span>
                      {changed && (
                        <span className="text-[10px] font-semibold tracking-normal px-1.5 py-0.5 bg-accent/40 text-foreground rounded-full">
                          Changed
                        </span>
                      )}
                    </button>
                    <div className="inline-flex items-center gap-1 p-1 border border-border rounded-full bg-muted/40 self-start sm:self-auto shrink-0">
                      {(["keep", "replace"] as const).map((d) => {
                        const active = current === d;
                        const isAi = aiPick === d;
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setUserDecisions((prev) => ({ ...prev, [item.name]: d }))}
                            className={`relative inline-flex items-center gap-1 px-3 py-1 text-[11px] font-semibold tracking-normal rounded-full transition-colors ${
                              active
                                ? "bg-selected text-selected-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                            aria-pressed={active}
                          >
                            {d === "keep" ? "Keep & Style" : "Replace"}
                            {isAi && !active && (
                              <span className="text-[9px] font-normal opacity-70 normal-case tracking-normal">
                                AI
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="mt-3 pl-6 text-xs sm:text-sm text-muted-foreground leading-relaxed bg-muted/30 p-3 border border-border">
                      {item.reasoning || (
                        <span className="italic">
                          {aiPick === "keep"
                            ? "Huelo suggested keeping this piece — it works with your chosen direction."
                            : "Huelo suggested replacing this piece — a better-fit option could elevate the design."}
                        </span>
                      )}
                      <div className="mt-2 text-[11px] text-muted-foreground/70">
                        AI suggestion: <span className="font-semibold tracking-normal">{aiPick === "keep" ? "Keep & Style" : "Replace"}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {hasDecisionChanges && onRegenerateVariant && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-border bg-muted/30">
              <p className="text-xs sm:text-sm text-muted-foreground">
                You changed some picks. Regenerate {activeVariant.label} to apply them.
              </p>
              <button
                type="button"
                onClick={regenWithPicks}
                disabled={!!regeneratingVariantId}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold tracking-normal bg-gradient-cta text-primary-foreground shadow-elegant hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {regeneratingVariantId === activeVariantId ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5" />
                )}
                {regeneratingVariantId === activeVariantId ? "Regenerating…" : "Regenerate with my picks"}
              </button>
            </div>
          )}
        </motion.div>
      )}


      {/* Variations tab — every plan: make a new layout for 1 iteration token */}
      {activeTab === "variations" && (() => {
        const others = variants.filter((v) => v.id !== activeVariantId);
        const left = Math.max(0, refineLimit - refineUsed);
        const createLayout = async () => {
          try {
            track("variation_created", { design_id: designId ?? null, source: "more_variations_tab" });
          } catch {}
          try {
            await submitRefinement({
              promptText:
                "Create a different furniture layout for this room: rearrange seating, zones and the focal grouping. Keep the same style, palette, materials and lighting.",
              baseImageUrl: activeVariant.image,
              variantId: activeVariantId,
            });
            toast({ title: "New layout ready", description: "You'll find it with your refinements." });
          } catch (e: any) {
            toast({ title: "Couldn't create a variation", description: e?.message, variant: "destructive" });
          }
        };
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-foreground" />
              <h2 className="text-lg md:text-2xl font-display text-foreground">More variations</h2>
            </div>
            <p className="text-muted-foreground text-sm">
              Get a new furniture layout for this room in the same style. Each variation uses 1 iteration token ({left} of {refineLimit} left this month).
            </p>
            <div className="flex flex-wrap items-center gap-3">
              {left > 0 ? (
                <button
                  type="button"
                  onClick={createLayout}
                  disabled={refining}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground text-[13px] font-semibold tracking-normal shadow-elegant hover:opacity-95 transition-opacity disabled:opacity-60"
                >
                  {refining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {refining ? "Creating variation…" : "Create a variation"}
                </button>
              ) : (
                <Link
                  to={pricingHref}
                  onClick={() => {
                    try { track("variations_upsell_clicked", { design_id: designId ?? null, source: "more_variations_tab" }); } catch {}
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground text-[13px] font-semibold tracking-normal shadow-elegant hover:opacity-95 transition-opacity"
                >
                  <Lock className="w-4 h-4" /> Get more iteration tokens
                </Link>
              )}
            </div>
            {others.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 pt-2">
                {others.map((v) => {
                  const isCloning = cloningVariantId === v.id;
                  return (
                    <div key={v.id} className="border border-border overflow-hidden flex flex-col">
                      <div className="relative aspect-video bg-muted">
                        <img src={v.image} alt={v.label} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4 flex flex-col gap-3 flex-1">
                        <div className="text-sm font-semibold text-foreground">{v.label}</div>
                        <button
                          type="button"
                          onClick={() => onCloneVariation?.(v.id)}
                          disabled={!!cloningVariantId}
                          className="mt-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-foreground text-foreground text-[12px] font-semibold tracking-normal hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
                        >
                          {isCloning && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          {isCloning ? "Opening…" : "Continue refining this design"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}


      {/* Always-visible NPS survey — mobile and desktop */}
      <div className="pt-6 border-t border-border">
        <NPSPrompt designId={designId} variant="tab" />
      </div>


      <div className="flex justify-center pt-4">
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-foreground text-foreground text-[13px] font-semibold tracking-normal hover:bg-foreground hover:text-background transition-colors"
        >
          <RotateCcw className="w-4 h-4" /> Start Over
        </button>
      </div>

      {/* Saved Items Sheet */}
      <SavedItemsSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        savedItems={savedItems}
        onRemove={removeItem}
        onClearAll={clearAll}
      />

      {/* Post-View save prompt */}
      <SaveAfterViewPrompt
        product={viewPromptProduct}
        onSave={() => {
          if (viewPromptProduct) {
            addItem(viewPromptProduct, { designId: designId ?? null, source: "view_prompt" });
            notifySaved();
            track("save_prompt_saved", {
              item_id: productItemId(viewPromptProduct),
              source: "view_click_prompt",
              design_id: designId ?? undefined,
            });
          }
          setViewPromptProduct(null);
        }}
        onDismiss={() => {
          if (viewPromptProduct) {
            track("save_prompt_dismissed", {
              item_id: productItemId(viewPromptProduct),
              source: "view_click_prompt",
              design_id: designId ?? undefined,
            });
          }
          setViewPromptProduct(null);
        }}
      />
    </motion.div>
  );
};

export default DesignResults;
